import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';

const AuthContext = createContext();

// Helper function to extract error message from various error formats
const extractErrorMessage = (errorData, fallbackMessage = 'An error occurred') => {
  if (!errorData) return fallbackMessage;

  // If it's already a string, return it
  if (typeof errorData === 'string') return errorData;

  // If it's an array, join the messages
  if (Array.isArray(errorData)) {
    return errorData.map(e => typeof e === 'string' ? e : JSON.stringify(e)).join(', ');
  }

  // If it's an object, extract messages from common error keys
  if (typeof errorData === 'object') {
    // Check for common Django REST Framework error keys
    if (errorData.non_field_errors) {
      return Array.isArray(errorData.non_field_errors)
        ? errorData.non_field_errors.join(', ')
        : errorData.non_field_errors;
    }
    if (errorData.detail) {
      return errorData.detail;
    }
    if (errorData.message) {
      return errorData.message;
    }
    if (errorData.error) {
      return typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
    }

    // Collect all field-level errors
    const fieldErrors = Object.entries(errorData)
      .filter(([key]) => key !== 'non_field_errors')
      .map(([field, errors]) => {
        const errorMessages = Array.isArray(errors) ? errors.join(', ') : errors;
        return `${field}: ${errorMessages}`;
      });

    if (fieldErrors.length > 0) {
      return fieldErrors.join('; ');
    }
  }

  return fallbackMessage;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgFeatures, setOrgFeatures] = useState({});

  // Fetch org-level feature toggle states
  const fetchOrgFeatures = useCallback(async () => {
    try {
      const response = await apiClient.get('/org-features/');
      setOrgFeatures(response.data.features || {});
    } catch (err) {
      // Silently fail — default to all features enabled
      setOrgFeatures({});
    }
  }, []);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await apiClient.get('/auth/user/');
          if (localStorage.getItem('token') === savedToken) {
            setUser(response.data.user);
            setToken(savedToken);
            fetchOrgFeatures();
          }
        } catch (err) {
          console.error('Auth check failed:', err);
          if (localStorage.getItem('token') === savedToken) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, [fetchOrgFeatures]);

  const register = async (username, email, password, password2, firstName = '', lastName = '', emailVerifiedToken = '', cnic = '') => {
    try {
      setError(null);
      const response = await apiClient.post('/auth/register/', {
        username,
        email,
        password,
        password2,
        first_name: firstName,
        last_name: lastName,
        email_verified_token: emailVerifiedToken,
        cnic,
      });
      const { user: userData, token: newToken } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true, user: userData, token: newToken };
    } catch (err) {
      const errorMsg = extractErrorMessage(
        err.response?.data?.errors || err.response?.data,
        err.message || 'Registration failed'
      );
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const login = async (username, password) => {
    try {
      setError(null);
      const response = await apiClient.post('/auth/login/', {
        username,
        password,
      });
      const data = response.data;

      // TOTP required — return pending token to the caller; don't set session yet
      if (data.status === 'totp_required') {
        return { success: false, totpRequired: true, totpToken: data.totp_token };
      }

      const { user: userData, token: newToken, expires_at } = data;
      localStorage.setItem('token', newToken);
      if (expires_at) {
        localStorage.setItem('tokenExpiresAt', expires_at);
      }
      setToken(newToken);
      setUser(userData);
      fetchOrgFeatures();
      return { success: true, user: userData, token: newToken, expiresAt: expires_at };
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = extractErrorMessage(
        err.response?.data?.errors || err.response?.data,
        err.response?.data?.message || 'Invalid username or password'
      );
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const loginWithTotpToken = async (totpPendingToken, code) => {
    try {
      const response = await apiClient.post('/auth/totp_verify_login/', {
        totp_token: totpPendingToken,
        code,
      });
      const { user: userData, token: newToken, expires_at } = response.data;
      localStorage.setItem('token', newToken);
      if (expires_at) {
        localStorage.setItem('tokenExpiresAt', expires_at);
      }
      setToken(newToken);
      setUser(userData);
      fetchOrgFeatures();
      return { success: true, expiresAt: expires_at };
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Invalid code. Please try again.';
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout/');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('_loginOrg');
      setToken(null);
      setUser(null);
      setOrgFeatures({});
      setError(null);
    }
  };

  /**
   * Returns true if the feature is enabled at the org level.
   * Features with no TenantFeature record default to enabled.
   */
  const orgFeatureEnabled = (featureName) => {
    if (!(featureName in orgFeatures)) return true;
    return orgFeatures[featureName];
  };

  /**
   * Returns true if the currently logged-in user has the given feature
   * assigned to their role.  Works regardless of whether user.features
   * contains objects ({id, name}) or plain strings.
   */
  const hasFeature = (featureName) => {
    if (!user || !featureName) return false;
    const target = featureName.toLowerCase();
    const matchFn = (f) => (typeof f === 'string' ? f : f?.name ?? '').toLowerCase() === target;
    // Primary: user.features (populated by get_features)
    if (Array.isArray(user.features) && user.features.some(matchFn)) return true;
    // Fallback: user.role.features (from RoleSerializer, no is_active gate)
    if (Array.isArray(user.role?.features) && user.role.features.some(matchFn)) return true;
    return false;
  };

  /**
   * Returns true if the user is a Django staff/superuser or has a role
   * that carries role_read / role_update (admin-level governance features).
   * Prefer feature-based checks over checking role.name === 'admin'.
   */
  const isAdmin = !!(
    user?.is_staff ||
    user?.is_superuser ||
    user?.role?.name === 'admin' ||
    user?.role === 'admin'
  );

  const isAuthenticated = !!token && !!user;

  // Expose a setter so child components (e.g. Profile) can update the
  // user object in real time without waiting for a full page refresh.
  const updateUser = useCallback((updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        isAuthenticated,
        isAdmin,
        hasFeature,
        orgFeatures,
        orgFeatureEnabled,
        fetchOrgFeatures,
        register,
        login,
        loginWithTotpToken,
        logout,
        setError,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
